// Phase 8 R2 — admin list of canned jobs (includes inactive).
// Separate query key from R1's useCannedJobs to avoid cache collisions.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ActiveFilter = 'all' | 'active' | 'inactive';

export interface CannedJobAdminItem {
  id: string;
  shop_id: string;
  name: string;
  description: string | null;
  category: string | null;
  is_active: boolean;
  is_favorite: boolean;
  favorite_order: number;
  line_count: number;
  updated_at: string;
}

export function useCannedJobsAdmin(
  shopId: string | undefined,
  opts?: { search?: string; category?: string | null; active?: ActiveFilter },
) {
  const search = opts?.search?.trim() ?? '';
  const category = opts?.category ?? null;
  const active = opts?.active ?? 'all';

  return useQuery({
    queryKey: ['canned-jobs-admin', shopId, search, category, active],
    enabled: !!shopId,
    queryFn: async (): Promise<CannedJobAdminItem[]> => {
      let q = (supabase as any)
        .from('canned_jobs')
        .select(
          'id, shop_id, name, description, category, is_active, is_favorite, favorite_order, updated_at, canned_job_lines(count)',
        )
        .eq('shop_id', shopId)
        .order('is_favorite', { ascending: false })
        .order('favorite_order', { ascending: true })
        .order('name', { ascending: true });

      if (search) q = q.ilike('name', `%${search}%`);
      if (category) q = q.eq('category', category);
      if (active === 'active') q = q.eq('is_active', true);
      if (active === 'inactive') q = q.eq('is_active', false);

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((r: any) => ({
        id: r.id,
        shop_id: r.shop_id,
        name: r.name,
        description: r.description ?? null,
        category: r.category ?? null,
        is_active: !!r.is_active,
        is_favorite: !!r.is_favorite,
        favorite_order: Number(r.favorite_order ?? 0),
        updated_at: r.updated_at,
        line_count: Array.isArray(r.canned_job_lines)
          ? Number(r.canned_job_lines[0]?.count ?? 0)
          : 0,
      }));
    },
  });
}
